import React from "react";
import { Instagram } from "lucide-react";
import Logo from "../Logo";
import { SOCIAL } from "@/lib/api";

const DiscordIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3.1a.07.07 0 0 0-.073.035c-.211.375-.445.864-.609 1.249a18.27 18.27 0 0 0-5.487 0 12.51 12.51 0 0 0-.617-1.249.075.075 0 0 0-.074-.035 19.74 19.74 0 0 0-3.76 1.269.07.07 0 0 0-.032.027C2.533 7.96 1.834 11.45 2.18 14.892a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.992 3.029.077.077 0 0 0 .084-.028c.462-.63.873-1.295 1.226-1.994a.076.076 0 0 0-.041-.105 13.11 13.11 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.794 8.18 1.794 12.061 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.04.106c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.029.077.077 0 0 0 .031-.055c.5-3.977-.838-7.44-3.548-10.497a.061.061 0 0 0-.031-.027zM8.02 13.331c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.956-2.42 2.157-2.42 1.21 0 2.176 1.097 2.157 2.42 0 1.334-.956 2.42-2.157 2.42zm7.974 0c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.955-2.42 2.157-2.42 1.21 0 2.176 1.097 2.157 2.42 0 1.334-.946 2.42-2.157 2.42z" />
  </svg>
);

const LINKS = [
  { label: "Growth", href: "#divisions" },
  { label: "Intelligence", href: "#divisions" },
  { label: "Protection", href: "#divisions" },
  { label: "Membership", href: "#membership" },
  { label: "Contact", href: "#contact" },
];

export const Footer = () => {
  return (
    <footer
      data-testid="footer"
      className="relative bg-black text-white border-t border-zinc-900 pt-20 pb-10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          <div className="md:col-span-6">
            <Logo size="md" />
            <p className="mt-8 font-mono text-[10px] tracking-[0.4em] uppercase text-zinc-500">
              Private Digital Operations
            </p>
            <p className="mt-3 font-mono text-[10px] tracking-[0.4em] uppercase text-zinc-500">
              Growth · Intelligence · Protection
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-6">
              Navigate
            </div>
            <ul className="space-y-3">
              {LINKS.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                    className="font-display text-lg text-zinc-300 hover:text-white transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-6">
              Connect
            </div>
            <div className="flex items-center gap-4">
              <a
                href={SOCIAL.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-social-link-instagram"
                aria-label="Instagram"
                className="w-12 h-12 flex items-center justify-center border border-zinc-800 hover:border-white hover:bg-white hover:text-black transition-colors duration-300"
              >
                <Instagram size={18} strokeWidth={1.5} />
              </a>
              <a
                href={SOCIAL.discord}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-social-link-discord"
                aria-label="Discord"
                className="w-12 h-12 flex items-center justify-center border border-zinc-800 hover:border-white hover:bg-white hover:text-black transition-colors duration-300"
              >
                <DiscordIcon className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-zinc-900 flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="font-mono text-[10px] tracking-[0.4em] uppercase text-zinc-500">
            © CAMO · All Rights Reserved
          </span>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 justify-center">
            <a href="/legal/terms" data-testid="footer-legal-terms" className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-white transition">Terms</a>
            <a href="/legal/privacy" data-testid="footer-legal-privacy" className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-white transition">Privacy</a>
            <a href="/legal/refund" data-testid="footer-legal-refund" className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-white transition">Refund</a>
            <a href="/legal/cookies" data-testid="footer-legal-cookies" className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-white transition">Cookies</a>
            <a href="/admin/login" data-testid="footer-admin-login" className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-600 hover:text-white transition">Admin</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
